import React, { useEffect, useState } from 'react';
import { ReptoolModal } from '../../utilities/ReptoolModal.tsx';
import { ReptileTile } from '../../utilities/ReptileTile.tsx'
import { MorphSelect } from '../../utilities/MorphSelect.tsx';
import { Tab, Tabs } from 'react-bootstrap';

import { CollectionReptile, TreeIndividual } from '../types/CollectionTypes';
import { Morph, blankMorph } from '../../../types/Morph.ts';
import { BasicFetch } from '../../../utils/BasicFetch.ts';
import { driver } from 'driver.js';
import { Alert } from 'react-bootstrap';
import { MorphGeneInputs } from '../../utilities/MorphGeneInputs.tsx';
import { Gene, blankGene } from '../../../types/Gene.ts';
import { useRepToolContext } from '../../../RepToolContext.tsx';

export interface OpenCreateModalInfo {
  childCollectionIndivId: number,
  sex: string
}

const driverSteps = (showPick: boolean, showGeneric: boolean, showExisting: boolean) => {
  if (showPick) {
    return [
      { element: '#unlisted-add-btn', popover: { title: 'Add Unlisted Reptile', description: 'Add a reptile to your family tree that is not in your collection.', position: 'bottom' } },
      { element: '#existing-add-btn', popover: { title: 'Add Existing Reptile', description: 'Select a reptile from your collection to add to your family tree.', position: 'bottom' } },
    ]
  } else if (showGeneric) {
    return [
      { element: '#tree-modal #morph', popover: { title: 'Morph', description: 'Select the morph of the reptile you are adding.', position: 'bottom' } },
      { element: '#tree-breeder', popover: { title: 'Breeder', description: 'Type the breeder of the reptile you are adding.', position: 'bottom' } },
    ]
  } else if (showExisting) {
    return [
      { element: '#existing-add', popover: { title: 'Select Reptile', description: 'Select a reptile from your collection to add to your family tree.', position: 'bottom' } },
    ]
  }
}

export const FamilyTreeEditModal = ({
  closeModal,
  collectionReptile,
  setReptile,
  creatingFamilyInfo,
  genes,
  morphs,
}: {
  closeModal: () => void,
  collectionReptile: CollectionReptile,
  setReptile: (reptile: CollectionReptile) => void,
  creatingFamilyInfo: OpenCreateModalInfo,
  genes: Gene[],
  morphs: Morph[]
}) => {
  const [showPick, setShowPick] = useState<boolean>(true);
  const [showGeneric, setShowGeneric] = useState<boolean>(false);
  const [showExisting, setShowExisting] = useState<boolean>(false);
  const [unlistMorph, setUnlistMorph] = useState<Morph>(blankMorph);
  const [unlistGenes, setUnlistGenes] = useState<Gene[]>([blankGene]);
  const [unlistName, setUnlistName] = useState<string>('');
  const [unlistBreeder, setUnlistBreeder] = useState<string>('');
  const [allReptiles, setAllReptiles] = useState<CollectionReptile[]>([]);
  const [fetchingData, setFetchingData] = useState<boolean>(true);
  const [activeUnlistedTab, setActiveUnlistedTab] = useState<'morph' | 'gene'>('morph');
  const {appendControls } = useRepToolContext();
  const driverObj = driver({
    steps: driverSteps(showPick, showGeneric, showExisting)
  })

  const fetchReptiles = () => {
    BasicFetch({
      url: `${(window as any).serverLocation}/collection_reptile/get_all`,
      extra: {
        sex: creatingFamilyInfo.sex,
        reptile: localStorage.getItem('selectedReptile'),
        use_new: true
      },
      method: 'GET',
      onSuccess: ({ reptiles }) => {
        setAllReptiles(reptiles || []);
        setFetchingData(false);
      },
      onFail: () => {
        alert('failed to fetch reptiles');
      },
      onFinish: () => {
        setFetchingData(false);
      }
    })
  }

  useEffect(() => {
    if (fetchingData) {
      fetchReptiles();
    }
  }, [fetchingData])

  useEffect(() => {
    appendControls(null, 'Add Family Member Modal');
  }, [])

  const selectExistReptile = (reptile: CollectionReptile) => {
    BasicFetch({
      url: `${(window as any).serverLocation}/collection_reptile/tree/${collectionReptile.id}/family/${creatingFamilyInfo.childCollectionIndivId}/create_parent`,
      method: 'POST',
      extra: {
        sex: creatingFamilyInfo.sex,
        collection_reptile_id: reptile.id
      },
      onSuccess: ({ reptile }) => {
        setReptile(reptile);
        closeModal();
      },
      onFail: () => {
        alert('failed to add reptile to tree');
      }
    })
  }
  const selectUnlistedReptile = () => {
    if (activeUnlistedTab === 'morph' && !unlistMorph.morph_name_id) {
      alert('Please select a valid morph from the list.');
      return;
    }

    BasicFetch({
      url: `${(window as any).serverLocation}/collection_reptile/tree/${collectionReptile.id}/family/${creatingFamilyInfo.childCollectionIndivId}/create_parent`,
      method: 'POST',
      data: {
        sex: creatingFamilyInfo.sex,
        morph: unlistMorph.name,
        name: unlistName,
        breeder: unlistBreeder,
        morph_name_id: unlistMorph.morph_name_id,
        genes: unlistGenes.filter(gene => gene.gene_name !== "" || gene.id),
        img: unlistMorph && unlistMorph.pictures.length ? unlistMorph.pictures[0].url : '',
        useGenes: activeUnlistedTab === 'gene' ? true : false
      },
      onSuccess: ({ reptile }) => {
        setReptile(reptile);
        closeModal();
      },
      onFail: () => {
        alert('failed to add reptile to tree');
      }
    })
  }
  const unlistedPhoto = typeof unlistMorph !== "string" && unlistMorph && unlistMorph.pictures && unlistMorph?.pictures.length > 0 ? unlistMorph.pictures[0].url : 'images/notavailable.jpg';
  return (
    <ReptoolModal title={"Add Family Member"} id="tree-modal" showTutorial={() => driverObj.drive()} closeModal={closeModal} body={(
      <React.Fragment>
        {showPick && <div id="type-buttons" className="d-flex flex-column mt-5 ">
          <div id='unlisted-add-btn' className="button expand"
            onClick={() => { setShowGeneric(true); setShowPick(false) }}>
            Use Unlisted Reptile</div>
          <div id='existing-add-btn' className="button expand mt-3"
            onClick={() => { setShowExisting(true); setShowPick(false) }}>
            Use Reptile From My Collection</div>
        </div>}
        {showExisting && <div id='existing-add' className="row">
          {!fetchingData && allReptiles.length === 0 && <Alert>No {creatingFamilyInfo.sex} reptiles in your collection</Alert>}
          {allReptiles.filter(rep => collectionReptile.id !== rep.id).map((reptile, index) => {
            if (!reptile.morph_name) {
              return null
            }
            const morph_name = reptile.morph_name.split("het")[0];
            return (<ReptileTile key={index}
              clickEvent={() => selectExistReptile(reptile)} reptile={reptile}
              morphInfo={morphs.find(morph => morph.name === morph_name)}
              buttonText="Select"
            />)
          })}
        </div>}
        {showGeneric && <div id='generic-add' className='d-flex flex-column no-pad'>
          <div className="text-center" >
            <div
              id="tree-preview-img"
              className='mx-auto'
              style={{
                backgroundImage: `url(${unlistedPhoto})`,
                backgroundSize: 'contain',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                width: 300,
                height: 300
              }} />
          </div>
          <div>
            <div className="collection-field">
              <h6>Name (optional)</h6>
              <h3 id="name">
                <div id='tree-morph-container'
                  className="ui-textinput ui-corner-all ui-shadow-inset ui-textinput-text ui-body-inherit">
                  <input
                    type='text'
                    placeholder='Name'
                    className='form-control'
                    value={unlistName}
                    onChange={(event) => setUnlistName(event.target.value)}
                  />
                </div>
              </h3>
            </div>
            <div className="small-6 collection-field weight no-pad columns">
              <h6>Breeder (optional)</h6>
              <h3 id="breeder">
                <div className="ui-textinput ui-corner-all ui-shadow-inset ui-textinput-text ui-body-inherit">
                  <input id="tree-breeder" className="breeder form-control" value={unlistBreeder} onChange={(event) => setUnlistBreeder(event.target.value)} placeholder="Breeder Name" type="text" />
                </div>
              </h3>
            </div>

            <div className='mt-5'>
              <h6>Parent genetics: </h6>
              <Tabs defaultActiveKey={activeUnlistedTab} id="parent-genetics" onSelect={(value) => value === "gene" || value === "morph" ? setActiveUnlistedTab(value) : false} className="mx-auto">
                <Tab eventKey="morph" title="Morph">
                  <MorphSelect
                    morphs={morphs}
                    morphTyped={(morph) => setUnlistMorph(morph)}
                    getCurrentText={(text) => setUnlistMorph(text)}
                    morph={unlistMorph}
                    id={'add-morph-select'}
                    showError={false}
                  />
                </Tab>
                <Tab eventKey={"gene"} title={"Genes"}>
                  <MorphGeneInputs
                    genes={genes}
                    setMorphName={(name) => { }}
                    morphs={morphs}
                    presetGenes={unlistGenes}
                    setCurrentGenes={(genes) => setUnlistGenes(genes)}
                  />
                </Tab>
              </Tabs>
            </div>
          </div>
          <div className="text-center mt-3">
            <div id="add-to-tree" onClick={() => selectUnlistedReptile()} className="button">Save Details</div>
          </div>
        </div>}
      </React.Fragment >
    )} />
  )

}